/**
 * UAPC Anytime - Shopping Cart Page
 * @module CartPage
 * @author Affan - Mobile Developer
 * @description Shopping cart with Stripe checkout
 * @version 1.0.0
 * Git: Affan <affan@linesoft.com> - Cart and checkout
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, CreditCard, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleQuantityChange = (partId, newQuantity) => {
    updateQuantity(partId, newQuantity);
  };

  const handleRemove = (partId) => {
    removeFromCart(partId);
  };

  const handleCheckout = async () => {
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/checkout/session`);
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="empty-cart">
          <ShoppingBag className="w-20 h-20 text-slate-300 mx-auto mb-6" />
          <h2 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase mb-4">
            Your Cart is Empty
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-md mx-auto">
            Looks like you haven't added any parts yet. Start browsing to find the parts you need.
          </p>
          <Link to="/search">
            <Button className="btn-industrial">
              Start Shopping
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="cart-page">
        {/* Back Link */}
        <Link 
          to="/search" 
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 mb-8"
          data-testid="back-to-shopping"
        >
          <ArrowLeft className="w-4 h-4" />
          Continue Shopping
        </Link>

        <h1 className="font-heading font-black text-4xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => (
              <div 
                key={item.part_id}
                className="flex gap-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
                data-testid={`cart-item-${index}`}
              >
                {/* Image */}
                <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-mono text-xs text-slate-400">{item.part_number}</span>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-slate-500 uppercase tracking-wider">
                    {item.part_number}
                  </div>
                  <Link to={`/parts/${item.part_id}`}>
                    <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white hover:text-blue-600 line-clamp-1">
                      {item.name}
                    </h3>
                  </Link>
                  <div className="font-heading font-bold text-lg text-blue-600 mt-1">
                    ${item.price.toFixed(2)}
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleQuantityChange(item.part_id, item.quantity - 1)}
                        className="w-8 h-8 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700"
                        data-testid={`qty-decrease-${index}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-12 h-8 flex items-center justify-center border-y border-slate-300 dark:border-slate-600 font-medium">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(item.part_id, item.quantity + 1)}
                        className="w-8 h-8 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700"
                        data-testid={`qty-increase-${index}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemove(item.part_id)}
                      className="text-red-600 hover:text-red-700 flex items-center gap-1 text-sm"
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </button>
                  </div>
                </div>

                {/* Item Total */}
                <div className="text-right">
                  <div className="font-heading font-bold text-xl text-slate-900 dark:text-white">
                    ${(item.price * item.quantity).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 sticky top-24">
              <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase mb-6">
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Subtotal ({cart.items.length} items)</span>
                  <span>${cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Tax</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mb-6">
                <div className="flex justify-between font-heading font-bold text-2xl text-slate-900 dark:text-white">
                  <span>Total</span>
                  <span data-testid="cart-total">${cart.total.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading || loading}
                className="w-full h-14 btn-industrial-orange"
                data-testid="checkout-btn"
              >
                {checkoutLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Proceed to Checkout
                  </span>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CartPage;
